/**
 * Simple Bundles - Infinite Options Image Swap v2 (17 Feb 2026)
 *
 * Swaps the main PDP image when a customer changes an Infinite Options
 * bundle dropdown. Uses the bundled_variants metafield for reliable
 * product/variant to image mapping. Should work across any Shopify theme.
 *
 * ------------------------------------------------------------------------
 * INSTALLATION (pick one):
 *
 *   Option A - Theme asset (recommended):
 *     1. Upload this file to your theme's assets/ folder
 *     2. Add before </body> in layout/theme.liquid:
 *        <script src="{{ 'sb-image-swap.js' | asset_url }}" defer></script>
 *
 *   Option B - Inline snippet:
 *     Wrap in <script>...</script> at the bottom of your product template
 *
 * ------------------------------------------------------------------------
 * HOW IT WORKS:
 *
 *   1. On page load, fetches the current product's metafields via
 *      /products/{handle}.json to get the bundled_variants data
 *   2. Builds a lookup map: option display text to { handle, variant_id }
 *   3. Fetches each child product once (cached) to get all variant images
 *   4. On dropdown change, looks up the selection and swaps the main image
 *
 *   Because it uses the actual metafield data (handles + variant IDs),
 *   it never has to guess product handles from display text.
 * ------------------------------------------------------------------------
 */

(function () {
  'use strict';

  // ========================================================================
  // CONFIG
  // ========================================================================
  const CONFIG = {
    // Selectors to find the main product image, tried in order. Most themes use these selectors.
    // Add your theme's selector at the top if none of these match.
    imageSelectors: [
      '.media.product-featured-image[data-source="main-product"] img.media-image',
      '.product__media-item--variant img',
      '.product__media img.image-magnify-full-size',
      '.product__media-list .product__media img',
      '.product__media img',
      '.product-image-main img',
      '.product__photos img',
      '.product__photo img.photoswipe__image',
      '.product__photo img',
      '.product-gallery__image img',
      '.product__main-photos img',
      '.product-main-slide .image-wrap img',
      '.product-featured-img',
      '.product-single__photo img',
      '.product-single__media img',
      '.product__media-item img',
      '[data-product-media-type="image"] img',
      '.product-single__image img',
      '.product__image-wrapper img',
    ],

    ioBundleContainer: '#simple-bundles-io-options',
    transitionMs: 200,
  };

  // ========================================================================
  // STATE
  // ========================================================================
  let mainImageEl = null;
  let originalSrc = null;
  let originalSrcset = null;
  let dropdownWatcherStarted = false;
  let documentListenerStarted = false;

  // Map: normalized option text to image URL
  // e.g. "cotton canvas cap" to "https://cdn.shopify.com/..."
  const imageMap = {};

  // Cache for /products/{handle}.js fetches
  const productCache = {};
  const selectState = new WeakMap();

  // ========================================================================
  // HELPERS
  // ========================================================================

  /**
   * Normalize option text for reliable matching.
   * Strips price suffixes like "(+CA$5.00)", whitespace, and lowercases.
   */
  function normalizeOptionText(text) {
    return String(text || '')
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '') // (+CA$5.00), (-$3.00), etc.
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * Find the main product image on the page.
   * Tries configured selectors, then falls back to largest image in product area.
   */
  function findMainImage() {
    for (const sel of CONFIG.imageSelectors) {
      const img = document.querySelector(sel);
      if (img && img.src) return img;
    }

    // Fallback: largest image in the product section
    const section =
      document.querySelector('.product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-single') ||
      document.querySelector('main');

    if (!section) return null;

    let best = null;
    let bestArea = 0;

    section.querySelectorAll('img').forEach((img) => {
      const area = img.offsetWidth * img.offsetHeight;

      if (area > bestArea && !img.closest(CONFIG.ioBundleContainer)) {
        best = img;
        bestArea = area;
      }
    });

    return best;
  }

  /**
   * Get the current product handle from the URL.
   */
  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Fetch product JSON (cached).
   */
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;

      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Resize a Shopify CDN image URL to a given width.
   */
  function resizeImage(url, width) {
    if (!url) return url;

    // Remove any existing size suffix and add desired one
    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  function toAbsoluteUrl(url) {
    if (!url) return url;
    if (url.startsWith('//')) return window.location.protocol + url;
    if (url.startsWith('/')) return window.location.origin + url;
    return url;
  }

  function removeShopifyWidthParam(url) {
    if (!url) return url;

    try {
      const parsedUrl = new URL(toAbsoluteUrl(url));
      parsedUrl.searchParams.delete('width');
      return parsedUrl.toString();
    } catch (e) {
      return toAbsoluteUrl(url)
        .replace(/([?&])width=\d+(&?)/, '$1')
        .replace(/[?&]$/, '');
    }
  }

  function buildShopifyWidthUrl(url, width) {
    const cleanUrl = removeShopifyWidthParam(url);

    try {
      const parsedUrl = new URL(cleanUrl);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (e) {
      const separator = cleanUrl.includes('?') ? '&' : '?';
      return `${cleanUrl}${separator}width=${width}`;
    }
  }

  function buildShopifySrcset(url) {
    const widths = [600, 700, 800, 900, 1000, 1200, 1400];

    return widths
      .map((width) => `${buildShopifyWidthUrl(url, width)} ${width}w`)
      .join(', ');
  }

  function getSelectedText(select) {
    return select && select.options && select.options[select.selectedIndex]
      ? select.options[select.selectedIndex].textContent.trim()
      : '';
  }

  function getSelectSnapshot(select) {
    return {
      value: select.value || '',
      index: String(select.selectedIndex),
      text: getSelectedText(select),
    };
  }

  function saveSelectSnapshot(select) {
    selectState.set(select, getSelectSnapshot(select));
  }

  function hasSelectChanged(select) {
    const previous = selectState.get(select);
    const current = getSelectSnapshot(select);

    if (!previous) return true;

    return (
      current.value !== previous.value ||
      current.index !== previous.index ||
      current.text !== previous.text
    );
  }

  /**
   * Swap the main image with a fade transition.
   */
  function swapImage(newSrc) {
    mainImageEl = findMainImage();

    if (!mainImageEl || !newSrc) return;

    const absoluteSrc = toAbsoluteUrl(newSrc);
    const srcForMain = buildShopifyWidthUrl(absoluteSrc, 1500);
    const srcsetForMain = buildShopifySrcset(absoluteSrc);

    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      mainImageEl.setAttribute('src', srcForMain);
      mainImageEl.setAttribute('srcset', srcsetForMain);
      mainImageEl.setAttribute('sizes', '100vw');
      mainImageEl.setAttribute('loading', 'eager');
      mainImageEl.setAttribute('fetchpriority', 'high');

      const show = () => {
        mainImageEl.style.opacity = '1';
      };

      mainImageEl.addEventListener('load', show, { once: true });

      // If cached / already loaded
      if (mainImageEl.complete) show();
    }, CONFIG.transitionMs);
  }

  function restoreOriginal() {
    mainImageEl = findMainImage();

    if (!mainImageEl || !originalSrc) return;

    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      mainImageEl.setAttribute('src', originalSrc);

      if (originalSrcset) {
        mainImageEl.setAttribute('srcset', originalSrcset);
      }

      mainImageEl.style.opacity = '1';
    }, CONFIG.transitionMs);
  }

  // ========================================================================
  // CORE: Build the image lookup map from bundled_variants metafield
  // ========================================================================

  /**
   * Parse the bundled_variants metafield and fetch images for each
   * child product/variant. Populates the global `imageMap`.
   *
   * bundled_variants structure (from SB metafields):
   * [
   *   {
   *     "variant_title": "Default Title",
   *     "variant_id": 123456,
   *     "handle": "sample_product_handle",
   *     "product_title": "Sample Product",
   *     "variant_options": [{ "name": "Option 1", "value": "Sample Product" }]
   *   },
   *   ...
   * ]
   *
   * The dropdown display text matches variant_options[].value
   * (e.g. "Sample Product", "Custom Water Bottle - Cornflower / Dark Berry")
   */
  async function buildImageMap(bundledVariants) {
    if (!Array.isArray(bundledVariants) || bundledVariants.length === 0) {
      return;
    }

    // Group by product handle to minimize fetches
    const handleGroups = {};

    for (const bv of bundledVariants) {
      if (!bv.handle) continue;
      if (!handleGroups[bv.handle]) handleGroups[bv.handle] = [];
      handleGroups[bv.handle].push(bv);
    }

    // Fetch each unique child product
    const fetches = Object.keys(handleGroups).map(async (handle) => {
      const product = await fetchProduct(handle);
      if (!product) return;

      for (const bv of handleGroups[handle]) {
        let imageSrc = null;

        // 1. Try to find variant-specific image by variant_id
        const matchedVariant = product.variants.find(
          (v) => String(v.id) === String(bv.variant_id)
        );

        if (matchedVariant && matchedVariant.featured_image) {
          imageSrc = matchedVariant.featured_image.src;
        }

        // 2. Fall back to product featured image
        if (!imageSrc && product.featured_image) {
          imageSrc = product.featured_image;
        }

        // 3. Fall back to first product image
        if (!imageSrc && product.images && product.images.length > 0) {
          imageSrc = product.images[0];
        }

        if (!imageSrc) continue;

        // Map all possible text keys for this variant
        const keys = new Set();

        // From variant_options (primary, matches dropdown display text)
        if (bv.variant_options) {
          for (const opt of bv.variant_options) {
            if (opt.value) keys.add(normalizeOptionText(opt.value));
          }
        }

        // From product_title + variant_title combo
        if (bv.product_title) {
          keys.add(normalizeOptionText(bv.product_title));

          if (bv.variant_title && bv.variant_title !== 'Default Title') {
            keys.add(
              normalizeOptionText(
                `${bv.product_title} - ${bv.variant_title}`
              )
            );
          }
        }

        for (const key of keys) {
          imageMap[key] = imageSrc;
        }
      }
    });

    await Promise.all(fetches);
  }

  /**
   * Get bundled_variants data. Tries multiple sources:
   *   1. Fetch from /products/{handle}.json (most reliable, works on any theme)
   *   2. Parse from page JSON-LD / script tags (fallback)
   */
  async function getBundledVariants() {
    const handle = getProductHandle();
    if (!handle) return null;

    // Fetch the full product JSON which includes metafields on some themes
    // But Shopify's /products/{handle}.json doesn't include metafields,
    // so we use the Storefront-compatible metafield endpoint instead.
    try {
      const res = await fetch(`/products/${handle}.json`);

      if (res.ok) {
        const data = await res.json();
        const product = data.product || data;

        // Check if metafields are embedded (some themes do this)
        if (product.metafields) {
          const sbMeta =
            product.metafields.simple_bundles ||
            product.metafields['app--4901177--simple_bundles'];

          if (sbMeta && sbMeta.bundled_variants) {
            return JSON.parse(sbMeta.bundled_variants);
          }
        }
      }
    } catch (e) {
      // Continue to next method
    }

    // Method 2: Look for metafield data in <script type="application/json"> tags
    const scripts = document.querySelectorAll('script[type="application/json"]');

    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);

        // Look for bundled_variants in various structures
        const searchObj = (obj, depth = 0) => {
          if (depth > 5 || !obj || typeof obj !== 'object') return null;

          if (obj.bundled_variants) {
            const val = obj.bundled_variants;
            return typeof val === 'string' ? JSON.parse(val) : val;
          }

          for (const key in obj) {
            const result = searchObj(obj[key], depth + 1);
            if (result) return result;
          }

          return null;
        };

        const found = searchObj(data);
        if (found) return found;
      } catch (e) {
        // Not the right JSON block
      }
    }

    // Method 3: Use Liquid-injected global (if the merchant added our snippet)
    if (window.__SB_BUNDLED_VARIANTS__) {
      return typeof window.__SB_BUNDLED_VARIANTS__ === 'string'
        ? JSON.parse(window.__SB_BUNDLED_VARIANTS__)
        : window.__SB_BUNDLED_VARIANTS__;
    }

    return null;
  }

  // ========================================================================
  // DROPDOWN EVENT HANDLING
  // ========================================================================

  function handleChange(select) {
    const selected = select.options[select.selectedIndex];

    if (!selected || !selected.value) {
      restoreOriginal();
      return;
    }

    const raw = selected.textContent;
    const normalized = normalizeOptionText(raw);

    const imageSrc = imageMap[normalized];

    if (imageSrc) {
      swapImage(imageSrc);
      return;
    }

    // No image mapped, try partial matching as a fallback
    const partialMatch = Object.keys(imageMap).find(
      (key) => normalized.includes(key) || key.includes(normalized)
    );

    if (partialMatch) {
      swapImage(imageMap[partialMatch]);
    }
  }

  function bindSelect(select) {
    if (!select || select.dataset.sbImgBound) return;

    select.dataset.sbImgBound = '1';
    saveSelectSnapshot(select);

    select.addEventListener('change', () => {
      saveSelectSnapshot(select);
      handleChange(select);
    });

    select.addEventListener('input', () => {
      saveSelectSnapshot(select);
      handleChange(select);
    });
  }

  function bindAllSelects() {
    const container = document.querySelector(CONFIG.ioBundleContainer);
    if (!container) return;

    const selects = container.querySelectorAll('select');

    selects.forEach((select) => {
      bindSelect(select);
    });
  }

  function startDropdownWatcher() {
    if (dropdownWatcherStarted) return;

    dropdownWatcherStarted = true;

    setInterval(() => {
      const container = document.querySelector(CONFIG.ioBundleContainer);
      if (!container) return;

      const selects = container.querySelectorAll('select');

      selects.forEach((select) => {
        bindSelect(select);

        if (hasSelectChanged(select)) {
          saveSelectSnapshot(select);
          handleChange(select);
        }
      });
    }, 300);
  }

  function startDocumentSelectListener() {
    if (documentListenerStarted) return;

    documentListenerStarted = true;

    document.addEventListener(
      'change',
      (event) => {
        const select = event.target && event.target.closest
          ? event.target.closest(`${CONFIG.ioBundleContainer} select`)
          : null;

        if (!select) return;

        saveSelectSnapshot(select);
        handleChange(select);
      },
      true
    );

    document.addEventListener(
      'input',
      (event) => {
        const select = event.target && event.target.closest
          ? event.target.closest(`${CONFIG.ioBundleContainer} select`)
          : null;

        if (!select) return;

        saveSelectSnapshot(select);
        handleChange(select);
      },
      true
    );
  }

  // ========================================================================
  // INIT
  // ========================================================================

  async function init() {
    // 1. Find main image
    mainImageEl = findMainImage();

    if (!mainImageEl) {
      setTimeout(() => {
        mainImageEl = findMainImage();

        if (mainImageEl) {
          originalSrc = mainImageEl.getAttribute('src');
          originalSrcset = mainImageEl.getAttribute('srcset');
        }
      }, 1000);
    } else {
      originalSrc = mainImageEl.getAttribute('src');
      originalSrcset = mainImageEl.getAttribute('srcset');
    }

    // 2. Get bundled_variants and build image map
    const bundledVariants = await getBundledVariants();

    if (bundledVariants) {
      await buildImageMap(bundledVariants);
    }

    // 3. Attach to existing dropdowns
    bindAllSelects();

    // 4. Watch dropdown changes
    startDocumentSelectListener();
    startDropdownWatcher();
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();